import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  endSession,
  getDailyDrivingTime,
  ApiResponse,
  ErrorResponse,
  TimeLogEntry,
  SessionActionResponse,
} from '../api/apiService';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { OdometerFormData } from '../lib/types';
import { getLocationString, isLocationUnavailableError, LOCATION_ERROR } from '../utils/locationUtils';
import { track, flush as flushAnalytics } from '../lib/analytics';

interface UseEndSessionProps {
  selectedVehicle: number | null;
  form: UseFormReturn<OdometerFormData>;
  onSessionEnded: () => void;
}

export const useEndSession = ({ selectedVehicle, form, onSessionEnded }: UseEndSessionProps) => {
  const { t } = useTranslation();
  const [isEndingSession, setIsEndingSession] = useState(false);
  const isEndingSessionMutexRef = useRef(false);


  const handleEndSessionConfirm: SubmitHandler<OdometerFormData> = async (data) => {
    if (isEndingSessionMutexRef.current) return false;
    isEndingSessionMutexRef.current = true;
    setIsEndingSession(true);

    try {
      if (!selectedVehicle) {
        form.setError('odometer', {
          type: 'manual',
          message: t('selectVehicle')
        });
        return false;
      }
      if (data.odometer === undefined || data.odometer === null || Number.isNaN(data.odometer) || data.odometer <= 0) {
        form.setError('odometer', {
          type: 'manual',
          message: t('odometerRequired')
        });
        return false;
      }

      track('driver_session_end_attempt', {
        vehicle_id: selectedVehicle,
      });

      const location = await getLocationString();
      const response: ApiResponse<SessionActionResponse | ErrorResponse> = await endSession(
        selectedVehicle,
        data.odometer,
        location,
        data.observations
      );
      if (response.ok) {
        onSessionEnded();

        track('driver_session_ended', {
          vehicle_id: selectedVehicle,
        });
        flushAnalytics();

        return true;
      } else {
        const errorData: any = response.data;

        if (errorData?.velocity_confirmation_required && errorData?.odometer_velocity_warning?.exceeds_limit) {
          track('driver_session_end_velocity_warning', {
            vehicle_id: selectedVehicle,
            warning: errorData.odometer_velocity_warning,
          });
          return {
            status: 'velocity_warning',
            warning: errorData.odometer_velocity_warning,
            location
          };
        }

        if (errorData?.odometer_decrease_detected && errorData?.previous_session_out) {
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            reason: 'odometer_decrease',
          });
          return {
            status: 'odometer_decrease',
            previousSessionOut: errorData.previous_session_out,
            newOdometer: data.odometer
          };
        }

        if (errorData.odometer) {
          let message = '';
          if (Array.isArray(errorData.odometer)) {
            message = errorData.odometer[0];
          } else if (typeof errorData.odometer === 'string') {
            try {
              let parsed: any = errorData.odometer;
              if (typeof parsed === 'string') {
                parsed = parsed.replace(/'/g, '"');
                parsed = JSON.parse(parsed);
              }
              if (Array.isArray(parsed)) {
                message = parsed[0];
              } else {
                message = errorData.odometer;
              }
            } catch {
              message = errorData.odometer;
            }
          }
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            reason: 'odometer_validation',
          });
          form.setError('odometer', {
            type: 'manual',
            message,
          });
        } else {
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            reason: 'unknown',
            status: response.status,
          });
          form.setError('odometer', {
            type: 'manual',
            message: t('errorEndingSession')
          });
        }
        return false;
      }
    } catch (error: any) {
      const isLocationUnavailable = isLocationUnavailableError(error);
      if (isLocationUnavailable) {
        track('driver_session_end_failed', {
          vehicle_id: selectedVehicle,
          reason: error.message === LOCATION_ERROR.SETTINGS_INVALID ? 'location_settings_invalid' : 'location_permission_denied',
        });
        form.setError('odometer', {
          type: 'manual',
          message: error.message,
        });
        return 'location_denied';
      }
      track('driver_session_end_failed', {
        vehicle_id: selectedVehicle,
        reason: 'exception',
      });
      console.error('Error ending session:', error);
      form.setError('odometer', {
        type: 'manual',
        message: t('errorEndingSession')
      });
      return false;
    } finally {
      isEndingSessionMutexRef.current = false;
      setIsEndingSession(false);
    }
  };

  // Allows ending a session when a valid WKT location is provided manually (e.g., via geocoding)
  const handleEndSessionConfirmWithProvidedLocation = async (data: OdometerFormData, locationWkt: string) => {
    if (isEndingSessionMutexRef.current) return false;
    isEndingSessionMutexRef.current = true;
    setIsEndingSession(true);

    try {
      if (!selectedVehicle) {
        form.setError('odometer', {
          type: 'manual',
          message: t('selectVehicle')
        });
        return false;
      }
      if (data.odometer === undefined || data.odometer === null || Number.isNaN(data.odometer) || data.odometer <= 0) {
        form.setError('odometer', {
          type: 'manual',
          message: t('odometerRequired')
        });
        return false;
      }

      track('driver_session_end_attempt', {
        vehicle_id: selectedVehicle,
        location_source: 'manual',
      });

      const response: ApiResponse<SessionActionResponse | ErrorResponse> = await endSession(
        selectedVehicle,
        data.odometer,
        locationWkt,
        data.observations
      );
      if (response.ok) {
        onSessionEnded();

        track('driver_session_ended', {
          vehicle_id: selectedVehicle,
          location_source: 'manual',
        });
        flushAnalytics();

        return true;
      } else {
        const errorData: any = response.data;

        if (errorData?.velocity_confirmation_required && errorData?.odometer_velocity_warning?.exceeds_limit) {
          track('driver_session_end_velocity_warning', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            warning: errorData.odometer_velocity_warning,
          });
          return {
            status: 'velocity_warning',
            warning: errorData.odometer_velocity_warning,
            location: locationWkt
          };
        }

        if (errorData?.odometer_decrease_detected && errorData?.previous_session_out) {
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'odometer_decrease',
          });
          return {
            status: 'odometer_decrease',
            previousSessionOut: errorData.previous_session_out,
            newOdometer: data.odometer
          };
        }

        if (errorData.odometer) {
          let message = '';
          if (Array.isArray(errorData.odometer)) {
            message = errorData.odometer[0];
          } else if (typeof errorData.odometer === 'string') {
            try {
              let parsed: any = errorData.odometer;
              if (typeof parsed === 'string') {
                parsed = parsed.replace(/'/g, '"');
                parsed = JSON.parse(parsed);
              }
              if (Array.isArray(parsed)) {
                message = parsed[0];
              } else {
                message = errorData.odometer;
              }
            } catch {
              message = errorData.odometer;
            }
          }
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'odometer_validation',
          });
          form.setError('odometer', {
            type: 'manual',
            message,
          });
        } else {
          track('driver_session_end_failed', {
            vehicle_id: selectedVehicle,
            location_source: 'manual',
            reason: 'unknown',
            status: response.status,
          });
          form.setError('odometer', {
            type: 'manual',
            message: t('errorEndingSession')
          });
        }
        return false;
      }
    } catch (error) {
      track('driver_session_end_failed', {
        vehicle_id: selectedVehicle,
        location_source: 'manual',
        reason: 'exception',
      });
      console.error('Error ending session with provided location:', error);
      form.setError('odometer', {
        type: 'manual',
        message: t('errorEndingSession')
      });
      return false;
    } finally {
      isEndingSessionMutexRef.current = false;
      setIsEndingSession(false);
    }
  };

  return {
    isEndingSession,
    handleEndSessionConfirm,
    handleEndSessionConfirmWithProvidedLocation
  };
};
